import React from "react";
import { Route } from "react-router-dom";
import AdminLayout from "./AdminLayout";

// Lazy-load all admin sub-pages for code splitting
const Dashboard = React.lazy(() => import("./Dashboard"));
const Users = React.lazy(() => import("./Users"));
const UsersAdd = React.lazy(() => import("./UsersAdd"));
const UsersUnvalidated = React.lazy(() => import("./UsersUnvalidated"));
const Groups = React.lazy(() => import("./Groups"));
const Events = React.lazy(() => import("./Events"));
const Content = React.lazy(() => import("./Content"));
const Settings = React.lazy(() => import("./Settings"));
const ProfileFields = React.lazy(() => import("./ProfileFields"));
const Pages = React.lazy(() => import("./Pages"));
const MenuBuilder = React.lazy(() => import("./MenuBuilder"));
const UserEdit = React.lazy(() => import("./UserEdit"));
const Notifications = React.lazy(() => import("./Notifications"));

// Suspense wrapper for lazy-loaded admin pages
function AdminSuspense({ children }) {
  return (
    <React.Suspense
      fallback={
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "#888",
            fontFamily: '"Roboto Slab", serif',
            fontSize: 14,
          }}
        >
          Loading...
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

/**
 * Generates the nested <Route> elements for the /administrator path.
 * To be used inside the main <Routes> component:
 *
 *   <Route path="administrator" element={<AdminLayout />}>
 *     {adminRoutes}
 *   </Route>
 *
 * Or import getAdminRouteElement() for the full <Route> tree.
 */
export const adminRoutes = (
  <>
    <Route
      index
      element={
        <AdminSuspense>
          <Dashboard />
        </AdminSuspense>
      }
    />
    <Route
      path="users"
      element={
        <AdminSuspense>
          <Users />
        </AdminSuspense>
      }
    />
    <Route
      path="users/add"
      element={
        <AdminSuspense>
          <UsersAdd />
        </AdminSuspense>
      }
    />
    <Route
      path="users/unvalidated"
      element={
        <AdminSuspense>
          <UsersUnvalidated />
        </AdminSuspense>
      }
    />
    <Route
      path="users/edit/:id"
      element={
        <AdminSuspense>
          <UserEdit />
        </AdminSuspense>
      }
    />
    <Route
      path="groups"
      element={
        <AdminSuspense>
          <Groups />
        </AdminSuspense>
      }
    />
    <Route
      path="events"
      element={
        <AdminSuspense>
          <Events />
        </AdminSuspense>
      }
    />
    <Route
      path="content"
      element={
        <AdminSuspense>
          <Content />
        </AdminSuspense>
      }
    />
    <Route
      path="settings"
      element={
        <AdminSuspense>
          <Settings />
        </AdminSuspense>
      }
    />
    <Route
      path="profilefields"
      element={
        <AdminSuspense>
          <ProfileFields />
        </AdminSuspense>
      }
    />
    <Route
      path="pages"
      element={
        <AdminSuspense>
          <Pages />
        </AdminSuspense>
      }
    />
    <Route
      path="menu-builder"
      element={
        <AdminSuspense>
          <MenuBuilder />
        </AdminSuspense>
      }
    />
    <Route
      path="notifications"
      element={
        <AdminSuspense>
          <Notifications />
        </AdminSuspense>
      }
    />
  </>
);

/**
 * Returns the complete admin Route element with layout and nested routes.
 * Can be dropped directly into a <Routes> block.
 */
export function getAdminRouteElement() {
  return (
    <Route path="administrator" element={<AdminLayout />}>
      {adminRoutes}
    </Route>
  );
}

export { AdminLayout };
export default AdminLayout;
